// src/shared/bus.ts — typed publish-subscribe event bus.
// Source: Phase 0 RESEARCH.md Pattern 3. ~40 lines, zero runtime deps.

type Handler<T> = (event: T) => void;

/**
 * EventBus<EventMap> — type-parameterised pub/sub.
 *
 *   const bus = new EventBus<GameEventMap>();
 *   const off = bus.on('entity-moved', (e) => { ... });
 *   bus.emit('entity-moved', { entityId, from, to, tick });
 *   off(); // unsubscribe
 *
 * - `emit` iterates a snapshot so unsubscribe-during-emit doesn't skip siblings.
 * - TypeScript enforces payload shape per event name via EventMap.
 */
export class EventBus<EventMap extends Record<string, unknown>> {
  private handlers: { [K in keyof EventMap]?: Array<Handler<EventMap[K]>> } = {};

  on<K extends keyof EventMap>(
    type: K,
    handler: Handler<EventMap[K]>
  ): () => void {
    const list = (this.handlers[type] ??= []);
    list.push(handler);
    return () => {
      const current = this.handlers[type];
      if (!current) return;
      this.handlers[type] = current.filter(
        (h) => h !== handler
      ) as typeof current;
    };
  }

  emit<K extends keyof EventMap>(type: K, event: EventMap[K]): void {
    const list = this.handlers[type];
    if (!list) return;
    // Iterate a shallow copy so unsubscribe-during-emit doesn't skip handlers.
    for (const h of list.slice()) h(event);
  }

  clear(): void {
    this.handlers = {};
  }
}
